<template>
  <div>
    <SMDialog ref="categorySwitchingRef">
      <template #header>{{ categorySwitchingDialog.title }}</template>
      <template #content>
        {{ categorySwitchingDialog.contentHtml }}
        <b v-if="categorySwitchingDialog.category">{{ categorySwitchingDialog.category }}</b>
        {{ categorySwitchingDialog.contentHtml2 }}
      </template>
      <template #actions>
        <SMButton type="primary" filled @click="onCategorySwitchingClose('cancel')">
          {{ categorySwitchingDialog.cancel }}
        </SMButton>
        <SMButton type="primary" filled @click="onCategorySwitchingClose('continue')">
          {{ categorySwitchingDialog.ok }}
        </SMButton>
      </template>
    </SMDialog>
    <SMDialog ref="sectionSwitchingRef">
      <template #header>{{ sectionSwitchingDialog.title }}</template>
      <template #content>
        {{ sectionSwitchingDialog.contentHtml }}
        <b v-if="sectionSwitchingDialog.section">{{ sectionSwitchingDialog.section }}</b>
        {{ sectionSwitchingDialog.contentHtml2 }}
        <b v-if="sectionSwitchingDialog.category">{{ sectionSwitchingDialog.category }}</b>
        {{ sectionSwitchingDialog.contentHtml3 }}
      </template>
      <template #actions>
        <SMButton type="secondary" filled @click="onSectionSwitchingClose('cancel')">
          {{ sectionSwitchingDialog.cancel }}
        </SMButton>
        <SMButton type="primary" filled @click="onSectionSwitchingClose('continue')">
          {{ sectionSwitchingDialog.ok }}
        </SMButton>
      </template>
    </SMDialog>
    <div id="title">
      {{ app.props.name }}
    </div>
    <div
      v-if="app.props.isShowCart"
      id="stats"
      :class="collapsed ? '!translate-x-full' : ''"
      class="bg-white border shadow rounded fixed right-2 top-16 z-50 w-72 max-h-[430px] p-4 flex flex-col gap-2 transition-transform translate-x-0 transition-duration-300"
    >
      <div v-if="app.props.isShowPromo" class="flex flex-col">
        <span class="font-bold text-center">Have a promo code?</span>

        <div class="flex gap-1">
          <SMInput v-model="promo" label="Promo Code" />
          <SMButton type="primary" filled @click="sendPromo">Apply</SMButton>
        </div>
      </div>
      <div v-if="showAlert" class="flex gap-1">
        <SMIcon icon="warning" />
        {{ alertmessage }}
      </div>
      <div v-if="hasSeats" class="flex flex-col gap-2">
        <span class="text-center font-bold">Selected seats</span>
        <div class="flex flex-col gap-1">
          <!-- <div  class="section" v-if="gaInCart.value.length">
            <i class="item-text">GA:</i>
          </div>
          <div :key="`s${index}`" v-for="(ga, index) in gaInCartForDisplay">
            {{ getGaSectionLabel(ga) }}
            {{ ga.label }} - {{ ga.count }} items
          </div>
          <div class="section" v-if="seatsInCart.value.length">
            <i class="item-text">Other:</i>
          </div>-->
          <div v-for="(seat, index) in seatsInCartForDisplay" :key="`g${index}`" class="flex gap-1">
            <span>{{ getSeatSectionLabel(seat) }}</span>
            <span>{{ (seat.row || seat.table).attributes.label }}</span>
            <span>{{ seat.attributes.label }}</span>
            <SMIcon icon="delete" class="ml-auto" @click.prevent.stop="() => removeSeatFromCart(seat)" />
          </div>
        </div>
      </div>
      <div v-if="app.props.isShowLegend" class="legend-list">
        <div
          v-for="(index, status) in availableStatus"
          :key="index"
          :class="['legend-color legend-' + statusColor(status)]"
        >
          {{ status }}
        </div>
      </div>
      <div v-if="app.props.isShowClose || app.props.isShowCheckout">
        <SMButton v-if="app.props.isShowClose" class="md-raised md-primary" @click="close">Close</SMButton>
        <SMButton v-if="app.props.isShowCheckout" class="md-raised md-primary" @click="checkout">Checkout</SMButton>
      </div>
      <button
        class="absolute top-2 w-6 -left-6 h-10 bg-white border border-r-0 rounded-l py-1"
        @click.prevent.stop="collapsed = !collapsed"
      >
        <SMIcon v-if="!collapsed" icon="keyboard_arrow_right" />
        <SMIcon v-if="collapsed" icon="keyboard_arrow_left" />
      </button>
    </div>
  </div>
</template>

<script setup>
import eventbus from 'tiny-emitter/instance';
import omit from 'lodash/omit';
import { TYPE, SEATSTATUS, TABLE } from '@/constants';
import { computed, onBeforeUnmount, onMounted, reactive, ref, shallowRef } from 'vue';
import Application from '@/core/application';
import { useActions, useState } from '@/store/useStore';
import SMIcon from '../ui/SMIcon.vue';
import SMDialog from '../ui/legacy/SMDialog.vue';
import SMInput from '../ui/SMInput.vue';
import SMButton from '../ui/SMButton.vue';

const app = Application.instance;

const collapsed = ref(app.isMobileDevice);
const promo = ref('');
const showAlert = ref(false);
const alertmessage = ref('');
const categorySwitchingDialog = reactive({
  title: 'Category Switching',
  ok: 'Continue',
  cancel: 'Cancel',
  contentHtml: 'You are about to switch to category ',
  contentHtml2: '. Your selection will be cleared.',
  category: '',
  unit: shallowRef(null),
});
const sectionSwitchingDialog = reactive({
  title: 'Section Switching',
  ok: 'Continue',
  cancel: 'Cancel',
  contentHtml: 'You are about to switch to section',
  contentHtml2: 'of category ',
  contentHtml3: '. Your selection will be cleared.',
  category: '',
  unit: shallowRef(null),
});

const categorySwitchingRef = ref(null);
const sectionSwitchingRef = ref(null);

const { gaInCart, seatsInCart, hasGap } = useState('renderer');
const { categories } = useState('categories');

const hasSeats = computed(() => {
  return (seatsInCart.value && seatsInCart.value.length) || gaInCart.value.length;
});
const availableStatus = computed(() => {
  return omit(SEATSTATUS, ['RESERVED', 'LOCKED']);
});
const seatsSelectedThisTime = computed(() => {
  return seatsInCartForDisplay.value.filter((s) => s.attributes.status === SEATSTATUS.SELECTED);
});
const seatsInCartForDisplay = computed(() => {
  const seatsInCartForDisplay = [];
  if (seatsInCart.value && seatsInCart.value.length) {
    seatsInCart.value.forEach((s) => {
      const seat = app.seatmap.seatsAndTableSeats.find((u) => u.uuid && u.uuid === s);
      const section = app.seatmap.sections.find((s) => s.uuid && seat.sid && s.uuid === seat.sid);
      if (section) {
        seat.attributes.section = section;
      }
      seatsInCartForDisplay.push(seat);
    });
  }
  return seatsInCartForDisplay;
});
const gaInCartForDisplay = computed(() => {
  const gaInCartForDisplay = [];
  if (gaInCart.value && gaInCart.value.length) {
    gaInCart.value.forEach((g) => {
      const ga = app.seatmap.units.find((u) => u.uuid && u.uuid === g.uuid);
      if (ga) {
        const section = app.seatmap.sections.find((s) => s.uuid && ga.sid && s.uuid === ga.sid);
        if (section) {
          ga.attributes.section = section;
        }
        gaInCartForDisplay.push({ ...g, instance: ga });
      }
    });
  }
  return gaInCartForDisplay;
});

const { updateSeatsInCart, setHasGap } = useActions('renderer');

const sendPromo = () => {
  if (typeof app.props.onPromoSubmit === 'function') {
    app.props.onPromoSubmit(promo, seatsInCart.value, gaInCart.value);
  }
};
const statusColor = (status) => {
  return status.toLowerCase();
};
const getSeatSectionLabel = (seat) => {
  return seat.attributes.section &&
    seat.attributes.section.attributes.label &&
    seat.attributes.section.attributes.label.text
    ? seat.attributes.section.attributes.label.text
    : '';
};

const updateCartState = (selection = { items: [] }) => {
  selection.items = selection.items.filter(
    (u) => u.type !== TYPE.GA && u.type !== TYPE.SECTION && u.type !== TYPE.TABLE
  );
  const newSeatsInCart = [];
  selection.items.forEach((s) => {
    newSeatsInCart.push(s.uuid);
  });

  updateSeatsInCart(newSeatsInCart);
  if (typeof app.props.onSelectionChange === 'function') {
    app.props.onSelectionChange(
      seatsInCartForDisplay.value,
      gaInCartForDisplay.value,
      seatsSelectedThisTime.value,
      seatsSelectedThisTime.value.map((s) => app.seatmap.getSectionById(s.sid))
    );
  }
  // updateGapAlert();
};
const removeSeatFromCart = (seat) => {
  if (seat.type === TYPE.TABLESEAT && seat.table.attributes.isGrouped) {
    seat.table.seats.forEach((s) => {
      app.seatmap.selection.toggle(s, false);
      eventbus.emit('seat:deselected', s);
    });
  } else {
    const cartSeat = seat.attributes.status === SEATSTATUS.CART;
    app.seatmap.selection.toggle(seat, false);
    if (cartSeat) {
      seat.toggleSelectedClass();
    }
    eventbus.emit('seat:deselected', seat);
  }
  eventbus.emit('selection:updated', app.seatmap.selection);
};
const addPreselectedSeats = () => {
  const seats = app.seatmap.seatsAndTableSeats
    .filter((u) => u.attributes.status === SEATSTATUS.CART)
    .map((u) => u.uuid);
  updateSeatsInCart(seats);
};
const selectSeat = (seat) => {
  updateGapAlert();
  if (typeof app.props.onSeatSelect === 'function') {
    app.props.onSeatSelect(seat, promo, app.seatmap.getSectionById(seat.sid));
  }
};
const deselectSeat = (seat) => {
  updateGapAlert();
  if (typeof app.props.onSeatDeselect === 'function') {
    app.props.onSeatDeselect(seat, promo);
  }
};

// make sure that the seat as 2 free seats on each side
const checkSeatForGap2 = (seat) => {
  // skip gap detection for grouped seats
  if (seat.attributes._groupId) {
    return false;
  }

  const row = seat.row || seat.table;
  const seats = row.seats;

  const seatIndex = seats.indexOf(seat);

  const isTable = seat.type === TYPE.TABLESEAT;

  if (
    isTable &&
    seat.table.subtype === TYPE.TABLE_RECTANGULAR &&
    seat.table.attributes.seatsDirection === TABLE.RECTANGULAR.DIRECTION.TWOSIDES
  ) {
    return false;
  }

  // Four sides just acts like a circle when it comes to gaps
  const isCircularTable =
    isTable &&
    (seat.table.subtype === TYPE.TABLE_ROUNDED || seat.table.attributes.seatsDirection === TABLE.RECTANGULAR.DIRECTION.FOURSIDES);

  const leftSeatIndex1 = isCircularTable ? (seatIndex - 1 + seats.length) % seats.length : seatIndex - 1;
  const leftSeatIndex2 = isCircularTable ? (seatIndex - 2 + seats.length) % seats.length : seatIndex - 2;
  const rightSeatIndex1 = isCircularTable ? (seatIndex + 1) % seats.length : seatIndex + 1;
  const rightSeatIndex2 = isCircularTable ? (seatIndex + 2) % seats.length : seatIndex + 2;

  const leftSeat1 = seats[leftSeatIndex1];
  const leftSeat2 = seats[leftSeatIndex2];
  const rightSeat1 = seats[rightSeatIndex1];
  const rightSeat2 = seats[rightSeatIndex2];

  const leftSeat1IsFree =
    leftSeat1 && leftSeat1.attributes.status === SEATSTATUS.FREE && !leftSeat1.attributes.invisible;
  const leftSeat2IsOccupied =
    !leftSeat2 || leftSeat2.attributes.status !== SEATSTATUS.FREE || leftSeat2.attributes.invisible;
  const rightSeat1IsFree =
    rightSeat1 && rightSeat1.attributes.status === SEATSTATUS.FREE && !rightSeat1.attributes.invisible;
  const rightSeat2IsOccupied =
    !rightSeat2 || rightSeat2.attributes.status !== SEATSTATUS.FREE || rightSeat2.attributes.invisible;

  return (leftSeat1IsFree && leftSeat2IsOccupied) || (rightSeat1IsFree && rightSeat2IsOccupied);
};

const updateGapAlert = () => {
  if (!app.props.isGapAvoidance) return;

  const selection = app.seatmap.selection.items.filter((u) => u.type === TYPE.SEAT || u.type === TYPE.TABLESEAT);

  // check if there are any gaps in the selection
  setHasGap(selection.some(checkSeatForGap2));

  if (!hasGap.value) {
    hideAlert();
    return;
  }

  if (typeof app.props.onGapFound === 'function') {
    showAlert.value = true;
    alertmessage.value = 'Gap Found';
    app.props.onGapFound();
  }
};

// const showgapAlert = () => {
//   showAlert.value = true;
//   alertmessage.value = 'Gap Found';
//   if (typeof app.props.onGapFound === 'function') {
//     app.props.onGapFound();
//   }
// };
const switchCategoryFromOutside = (categoryId) => {
  if (app.props.warnOnSwitch && categoryId) {
    // const cat = categories.find((c) => c.id && c.id.toString().trim() === categoryId.toString().trim());
    let categoryChangeCallback = '';
    if (typeof app.props.onCategoryChange === 'function') {
      categoryChangeCallback = app.props.onCategoryChange;
    }
    document.getElementById('appspinner').style.display('');
    setTimeout(() => {
      eventbus.emit('switch:category:external', categoryId, categoryChangeCallback);
    }, 100);
  }
};
const showcategoryAlert = (unit) => {
  eventbus.emit('tooltip:hide');
  // showAlert = true;
  if (!unit.categoryId) {
    return;
  }
  app.refreshCategoryList(false, unit);
  const cat = categories.value.find((c) => c.id && c.id.toString().trim() === unit.categoryId.toString().trim());
  categorySwitchingDialog.unit = unit;

  if (!cat) {
    eventbus.emit('categoryAlertResponse', false);
    return;
  }

  categorySwitchingDialog.category = cat.name + ' (' + cat.price + ')';

  // We only need to show the dialog if the user has selected seats and warnOnSwitch is true
  const showDialog = app.props.warnOnSwitch && app.seatmap.selection.items.length > 0;
  const gaSwitch = unit.type === TYPE.GA && app.currentSeatSection && app.currentSeatSection.type === TYPE.GA;

  if (showDialog && !gaSwitch) {
    openDialog(categorySwitchingRef.value);
  } else {
    onCategorySwitchingClose('continue');
  }
};
const showsectionAlert = (unit) => {
  eventbus.emit('tooltip:hide');

  let sid = unit.sid;
  if (unit.type === TYPE.SECTION) {
    sid = unit.uuid;
  }

  if (!sid) {
    eventbus.emit('sectionAlertResponse', false);
    return;
  }

  app.refreshCategoryList(false, unit);
  const cat = unit.category;
  const section = app.seatmap.getSectionById(sid);
  sectionSwitchingDialog.unit = unit;

  if (!app.currentSeatSection || app.currentSeatSection.uuid === sid) {
    return onSectionSwitchingClose();
  }

  sectionSwitchingDialog.category = cat ? cat.name + ' (' + cat.price + ')' : undefined;
  sectionSwitchingDialog.section = section.attributes.label.text;

  // We only need to show the dialog if the user has selected seats and warnOnSwitch is true
  const showDialog = app.props.warnOnSwitch && app.seatmap.selection.items.length > 0;
  const gaSwitch = unit.type === TYPE.GA && app.currentSeatSection && app.currentSeatSection.type === TYPE.GA;

  if (showDialog && !gaSwitch) {
    openDialog(sectionSwitchingRef.value);
  } else {
    onSectionSwitchingClose('continue');
  }
};
const showposAlert = () => {
  showAlert.value = true;
  alertmessage.value = 'Seat belongs to different POS.';
};
const hideAlert = () => {
  showAlert.value = false;
  alertmessage.value = '';
};
let currentDialog = null;
const openDialog = (ref) => {
  currentDialog = ref;
  ref.open();
};
const closeDialog = (ref) => {
  currentDialog = null;
  if (ref != null) {
    ref.close();
  }
};
// for external category switching
const switchCategoryExternal = (categoryId, callbackFn) => {
  //remove next line to keep already selected items after category switch
  app.seatmap.clearSelection();
  const rendererContent = app.seatmap.toJSON();
  app.props.categoryID = categoryId;
  if (rendererContent) {
    app.seatmap.initSeatmap({ state: rendererContent }, true, app.seatmap.orders);
    if (callbackFn) {
      callbackFn(categoryId);
    }
    //setTimeout(() => {
    eventbus.emit('camera:zoom:fitsvg');
    //}, 300);
  }
};
// for internal category switching
const switchCategory = (categoryId, callbackFn, clickedSeat) => {
  //remove next line to keep already selected items after category switch
  app.seatmap.clearSelection();
  const rendererContent = app.seatmap.toJSON();
  app.props.categoryID = categoryId;
  if (rendererContent) {
    //app.seatmap.initSeatmap({ state: rendererContent }, true, app.seatmap.orders);
    app.seatmap.seatsAndTableSeats.forEach((u) => {
      if (u.categoryId === app.props.categoryID) {
        u.view.addClass('active-category');
      } else {
        u.view.removeClass('active-category');
      }
    });
    if (callbackFn) {
      callbackFn(categoryId);
    }
    //setTimeout(() => {
    //eventbus.emit('camera:zoom:fitsvg');
    //}, 300);
  }
};
const onCategorySwitchingClose = (type) => {
  let categoryId = null;
  let categoryChangeCallback = null;
  const unit = categorySwitchingDialog.unit;
  const isSeat = unit && (unit.type === TYPE.SEAT || unit.type === TYPE.TABLESEAT);
  if (type === 'continue' && unit && unit.category) {
    categoryId = unit.categoryId;
    if (typeof app.props.onCategoryChange === 'function') {
      categoryChangeCallback = app.props.onCategoryChange;
    }
    categorySwitchingDialog.unit = null;
    closeDialog(currentDialog);
    /*
        document.getElementById('appspinner').classList.remove('hide');
        */
    setTimeout(() => {
      eventbus.emit('switch:category', categoryId, categoryChangeCallback, isSeat ? unit : null);
      eventbus.emit('categoryAlertResponse', true);
    }, 100);
  } else {
    categorySwitchingDialog.unit = null;
    closeDialog(currentDialog);
    eventbus.emit('categoryAlertResponse', false);
  }
};
const onSectionSwitchingClose = (type) => {
  const { unit } = sectionSwitchingDialog;
  const category = unit.category;
  const section = unit.type === TYPE.SECTION ? unit : app.seatmap.getSectionById(unit.sid);

  const isSeat = unit && (unit.type === TYPE.SEAT || unit.type === TYPE.TABLESEAT);

  if (type === 'cancel') {
    sectionSwitchingDialog.unit = null;
    closeDialog(currentDialog);
    eventbus.emit('sectionAlertResponse', false);
    return;
  }

  const isSelected = app.toggleSelectedSection(section);

  // check if category would be changed
  if (category?.id && category.id.toString().trim() !== app.props.categoryID?.toString().trim()) {
    app.refreshCategoryList(false, sectionSwitchingDialog.unit);

    setTimeout(() => {
      eventbus.emit('switch:category', category?.id, app.props.onCategoryChange, isSeat ? unit : null);
      if (app.props.onSectionSwitch) {
        app.props.onSectionSwitch(section, isSelected);
      }
      eventbus.emit('sectionAlertResponse', true);
    }, 100);
  } else {
    app.seatmap.clearSelection();
    if (app.props.onSectionSwitch) {
      app.props.onSectionSwitch(section, isSelected);
    }
    eventbus.emit('sectionAlertResponse', true);
  }

  sectionSwitchingDialog.unit = null;
  closeDialog(currentDialog);
};
const close = () => {
  if (typeof app.props.onClose === 'function') {
    app.props.onClose();
  }
};
const checkout = () => {
  if (typeof app.props.onCheckout === 'function') {
    app.props.onCheckout(seatsInCartForDisplay, gaInCartForDisplay);
  }
};

eventbus.on('selection:updated', updateCartState, this);
eventbus.on('seat:selected', selectSeat, this);
eventbus.on('seat:deselected', deselectSeat, this);
document.addEventListener('switch:category:outside', switchCategoryFromOutside);
eventbus.on('show:categoryAlert', showcategoryAlert, this);
eventbus.on('show:sectionAlert', showsectionAlert, this);
eventbus.on('show:posAlert', showposAlert, this);
eventbus.on('hide:alert', hideAlert, this);
eventbus.on('renderer:addPreselectedSeats', addPreselectedSeats, this);
eventbus.on('switch:category', switchCategory, this);
eventbus.on('switch:category:external', switchCategoryExternal, this);

onBeforeUnmount(() => {
  eventbus.off('selection:updated', updateCartState, this);
  eventbus.off('seat:selected', selectSeat, this);
  eventbus.off('seat:deselected', deselectSeat, this);
  document.removeEventListener('switch:category:outside', switchCategoryFromOutside);
  eventbus.off('show:categoryAlert', showcategoryAlert, this);
  eventbus.off('show:sectionAlert', showsectionAlert, this);
  eventbus.off('show:posAlert', showposAlert, this);
  eventbus.off('hide:alert', hideAlert, this);
  eventbus.off('switch:category', switchCategory);
  eventbus.off('switch:category:external', switchCategoryExternal);
});
</script>

<style lang="scss" scoped>
svg,
svg g {
  touch-action: none !important;
}
#title {
  word-wrap: break-word;
  position: absolute;
  min-width: 300px;
  width: 300px;
  max-height: 370px;
}
#stats {
  &.mobile {
    transition: 0.5s ease all;
    width: 250px;
    &.collapsed {
      right: -250px;
      transition: 0.5s ease all;
    }
  }
}
.seats-container {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 204px;
}
.md-list {
  padding: 6px 0;
  margin-left: 1px;
  user-select: none;
}
input {
  padding: 15px 14px 15px;
}
label {
  margin-left: 14px;
}

.section {
  margin-top: 5px;
}

.md-button {
  align-self: flex-start;
  margin-top: 11px;
}
div {
  user-select: none;
  -webkit-user-select: none;
}
body {
  -moz-user-focus: ignore;
  -moz-user-select: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -o-user-select: none;
  user-select: none;
}
.center {
  left: 25%;
}
#seatmap svg {
  z-index: 55;
  background-color: #ffffff;
}
</style>
